import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import type { NormalizedAttachment } from '../../utils/parseEml';
import type { ConversionCategory } from '../../utils/detectFileCategory';
import type { UniversalConvertBundle } from '../../utils/convertUniversal';

interface Props {
  category: ConversionCategory;
  bundle: UniversalConvertBundle;
  onBundleChange: (b: UniversalConvertBundle) => void;
  sheetNames: string[];
  attachments: NormalizedAttachment[];
  disabled?: boolean;
  passwordEnabled: boolean;
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  onPasswordEnabled: (v: boolean) => void;
  onPassword: (v: string) => void;
  onConfirmPassword: (v: string) => void;
  onShowPassword: (v: boolean) => void;
  onShowConfirmPassword: (v: boolean) => void;
  passwordMismatch: boolean;
  passwordTooShort: boolean;
}

export const ConvertToPdfSettingsPanel: React.FC<Props> = ({
  category,
  bundle,
  onBundleChange,
  sheetNames,
  attachments,
  disabled,
  passwordEnabled,
  password,
  confirmPassword,
  showPassword,
  showConfirmPassword,
  onPasswordEnabled,
  onPassword,
  onConfirmPassword,
  onShowPassword,
  onShowConfirmPassword,
  passwordMismatch,
  passwordTooShort,
}) => {
  const g = bundle.global;

  const setGlobal = (partial: Partial<UniversalConvertBundle['global']>) => {
    onBundleChange({ ...bundle, global: { ...bundle.global, ...partial } });
  };

  return (
    <VStack align="stretch" spacing={1}>
      <Accordion allowMultiple defaultIndex={[]} borderRadius="xl">
        <AccordionItem border="none">
          <AccordionButton
            px={0}
            py={3}
            borderRadius="xl"
            _hover={{ bg: 'blackAlpha.50' }}
            _expanded={{ bg: 'transparent' }}
          >
            <Box flex="1" textAlign="left" fontWeight="500" fontSize="sm" color="gray.800">
              PDF settings
            </Box>
            <AccordionIcon color="gray.400" />
          </AccordionButton>
          <AccordionPanel px={0} pb={4} pt={0}>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.500" fontWeight="500">
                  Page size
                </FormLabel>
                <RadioGroup
                  value={g.pageSize}
                  onChange={(v) => setGlobal({ pageSize: v as typeof g.pageSize })}
                  isDisabled={disabled}
                >
                  <Stack spacing={2}>
                    <Radio value="auto" size="sm" colorScheme="purple">
                      Auto
                    </Radio>
                    <Radio value="a4" size="sm" colorScheme="purple">
                      A4
                    </Radio>
                    <Radio value="letter" size="sm" colorScheme="purple">
                      Letter
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.500" fontWeight="500">
                  Orientation
                </FormLabel>
                <RadioGroup
                  value={g.orientation}
                  onChange={(v) => setGlobal({ orientation: v as typeof g.orientation })}
                  isDisabled={disabled}
                >
                  <Stack spacing={2}>
                    <Radio value="auto" size="sm" colorScheme="purple">
                      Auto
                    </Radio>
                    <Radio value="portrait" size="sm" colorScheme="purple">
                      Portrait
                    </Radio>
                    <Radio value="landscape" size="sm" colorScheme="purple">
                      Landscape
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.500" fontWeight="500">
                  Quality
                </FormLabel>
                <RadioGroup
                  value={g.quality}
                  onChange={(v) => setGlobal({ quality: v as typeof g.quality })}
                  isDisabled={disabled}
                >
                  <Stack spacing={2}>
                    <Radio value="standard" size="sm" colorScheme="purple">
                      Standard
                    </Radio>
                    <Radio value="high" size="sm" colorScheme="purple">
                      High
                    </Radio>
                    <Radio value="print" size="sm" colorScheme="purple">
                      Print
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
          <AccordionButton
            px={0}
            py={3}
            borderRadius="xl"
            _hover={{ bg: 'blackAlpha.50' }}
            _expanded={{ bg: 'transparent' }}
          >
            <Box flex="1" textAlign="left" fontWeight="500" fontSize="sm" color="gray.800">
              Security
            </Box>
            <AccordionIcon color="gray.400" />
          </AccordionButton>
          <AccordionPanel px={0} pb={4} pt={0}>
            <VStack align="stretch" spacing={4}>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel htmlFor="pw-toggle" mb={0} fontSize="sm" fontWeight="500" color="gray.700">
                  Password-protect PDF
                </FormLabel>
                <Switch
                  id="pw-toggle"
                  colorScheme="purple"
                  size="sm"
                  isChecked={passwordEnabled}
                  onChange={(e) => onPasswordEnabled(e.target.checked)}
                  isDisabled={disabled}
                />
              </FormControl>
              {passwordEnabled && (
                <>
                  <FormControl isInvalid={passwordTooShort}>
                    <FormLabel fontSize="xs">Password</FormLabel>
                    <InputGroup size="sm">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => onPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <InputRightElement height="100%">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          size="xs"
                          variant="ghost"
                          icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                          onClick={() => onShowPassword(!showPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {passwordTooShort && (
                      <FormHelperText color="red.500">Minimum 6 characters.</FormHelperText>
                    )}
                  </FormControl>
                  <FormControl isInvalid={passwordMismatch}>
                    <FormLabel fontSize="xs">Confirm password</FormLabel>
                    <InputGroup size="sm">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => onConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <InputRightElement height="100%">
                        <IconButton
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          size="xs"
                          variant="ghost"
                          icon={showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
                          onClick={() => onShowConfirmPassword(!showConfirmPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {passwordMismatch && <FormHelperText color="red.500">Passwords must match.</FormHelperText>}
                  </FormControl>
                  <Text fontSize="xs" color="gray.500">
                    PDFs use AES-256 encryption in your browser before download.
                  </Text>
                </>
              )}
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
          <AccordionButton
            px={0}
            py={3}
            borderRadius="xl"
            _hover={{ bg: 'blackAlpha.50' }}
            _expanded={{ bg: 'transparent' }}
          >
            <Box flex="1" textAlign="left" fontWeight="500" fontSize="sm" color="gray.800">
              File-specific
            </Box>
            <AccordionIcon color="gray.400" />
          </AccordionButton>
          <AccordionPanel px={0} pb={2} pt={0}>
            <FileKindSettings
              category={category}
              bundle={bundle}
              onBundleChange={onBundleChange}
              sheetNames={sheetNames}
              attachments={attachments}
              disabled={disabled}
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </VStack>
  );
};

const FileKindSettings: React.FC<{
  category: ConversionCategory;
  bundle: UniversalConvertBundle;
  onBundleChange: (b: UniversalConvertBundle) => void;
  sheetNames: string[];
  attachments: NormalizedAttachment[];
  disabled?: boolean;
}> = ({ category, bundle, onBundleChange, sheetNames, attachments, disabled }) => {
  if (category === 'unsupported') {
    return (
      <Text fontSize="sm" color="gray.500">
        No file-specific settings.
      </Text>
    );
  }

  if (category === 'email' && bundle.email) {
    const e = bundle.email;
    const embeddable = attachments.filter((a) => a.embeddableInPdf);
    return (
      <VStack align="stretch" spacing={3}>
        <Checkbox isChecked={e.includeBody} onChange={(ev) => onBundleChange({ ...bundle, email: { ...e, includeBody: ev.target.checked } })} isDisabled={disabled} colorScheme="purple" size="sm">
          Message body
        </Checkbox>
        <Checkbox isChecked={e.includeHeaders} onChange={(ev) => onBundleChange({ ...bundle, email: { ...e, includeHeaders: ev.target.checked } })} isDisabled={disabled} colorScheme="purple" size="sm">
          Headers (From, To, Date…)
        </Checkbox>
        <Checkbox
          isChecked={e.includeAttachmentList}
          onChange={(ev) => onBundleChange({ ...bundle, email: { ...e, includeAttachmentList: ev.target.checked } })}
          isDisabled={disabled}
          colorScheme="purple"
          size="sm"
        >
          Attachment list
        </Checkbox>
        <Box>
          <Text fontSize="xs" fontWeight="500" color="gray.500" mb={2}>
            Embed in PDF
          </Text>
          {embeddable.length === 0 ? (
            <Text fontSize="xs" color="gray.400">
              No embeddable attachments detected.
            </Text>
          ) : (
            <CheckboxGroup
              value={e.embedAttachmentIds}
              onChange={(vals) => onBundleChange({ ...bundle, email: { ...e, embedAttachmentIds: vals as string[] } })}
            >
              <VStack align="stretch" spacing={1}>
                {embeddable.map((a) => (
                  <Checkbox key={a.id} value={a.id} size="sm" isDisabled={disabled}>
                    <Text fontSize="sm" noOfLines={1}>
                      {a.filename}
                    </Text>
                  </Checkbox>
                ))}
              </VStack>
            </CheckboxGroup>
          )}
        </Box>
      </VStack>
    );
  }

  if (category === 'document' && bundle.document) {
    const d = bundle.document;
    return (
      <VStack align="stretch" spacing={3}>
        <Checkbox isChecked={d.preserveFormatting} onChange={(ev) => onBundleChange({ ...bundle, document: { ...d, preserveFormatting: ev.target.checked } })} isDisabled={disabled} colorScheme="purple" size="sm">
          Preserve formatting
        </Checkbox>
        <Checkbox isChecked={d.includeImages} onChange={(ev) => onBundleChange({ ...bundle, document: { ...d, includeImages: ev.target.checked } })} isDisabled={disabled} colorScheme="purple" size="sm">
          Images
        </Checkbox>
        <Checkbox isChecked={d.keepLinks} onChange={(ev) => onBundleChange({ ...bundle, document: { ...d, keepLinks: ev.target.checked } })} isDisabled={disabled} colorScheme="purple" size="sm">
          Links
        </Checkbox>
        <FormControl>
          <FormLabel fontSize="xs">Margins</FormLabel>
          <RadioGroup value={d.margins} onChange={(v) => onBundleChange({ ...bundle, document: { ...d, margins: v as typeof d.margins } })} isDisabled={disabled}>
            <Stack direction="row" spacing={4}>
              <Radio value="narrow" size="sm">
                Narrow
              </Radio>
              <Radio value="normal" size="sm">
                Normal
              </Radio>
              <Radio value="wide" size="sm">
                Wide
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
      </VStack>
    );
  }

  if (category === 'text' && bundle.document) {
    const d = bundle.document;
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Margins</FormLabel>
          <RadioGroup value={d.margins} onChange={(v) => onBundleChange({ ...bundle, document: { ...d, margins: v as typeof d.margins } })} isDisabled={disabled}>
            <Stack direction="row" spacing={4} flexWrap="wrap">
              <Radio value="narrow" size="sm">
                Narrow
              </Radio>
              <Radio value="normal" size="sm">
                Normal
              </Radio>
              <Radio value="wide" size="sm">
                Wide
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
      </VStack>
    );
  }

  if (category === 'spreadsheet' && bundle.spreadsheet) {
    const s = bundle.spreadsheet;
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Sheet</FormLabel>
          <RadioGroup
            value={String(s.sheetIndex)}
            onChange={(v) => onBundleChange({ ...bundle, spreadsheet: { ...s, sheetIndex: parseInt(v, 10) } })}
            isDisabled={disabled || sheetNames.length === 0}
          >
            <Stack spacing={2}>
              {sheetNames.map((name, i) => (
                <Radio key={name} value={String(i)} size="sm">
                  {name}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>
        <Checkbox isChecked={s.fitPageWidth} onChange={(ev) => onBundleChange({ ...bundle, spreadsheet: { ...s, fitPageWidth: ev.target.checked } })} isDisabled={disabled}>
          Fit to page width
        </Checkbox>
        <Checkbox isChecked={s.gridlines} onChange={(ev) => onBundleChange({ ...bundle, spreadsheet: { ...s, gridlines: ev.target.checked } })} isDisabled={disabled}>
          Include gridlines
        </Checkbox>
        <Checkbox isChecked={s.repeatHeader} onChange={(ev) => onBundleChange({ ...bundle, spreadsheet: { ...s, repeatHeader: ev.target.checked } })} isDisabled={disabled}>
          Repeat header row
        </Checkbox>
      </VStack>
    );
  }

  if (category === 'presentation' && bundle.presentation) {
    const p = bundle.presentation;
    return (
      <VStack align="stretch" spacing={3}>
        <Checkbox isChecked={p.oneSlidePerPage} onChange={(ev) => onBundleChange({ ...bundle, presentation: { ...p, oneSlidePerPage: ev.target.checked } })} isDisabled={disabled}>
          One slide per page
        </Checkbox>
        <Checkbox
          isChecked={p.preserveAspectRatio}
          onChange={(ev) => onBundleChange({ ...bundle, presentation: { ...p, preserveAspectRatio: ev.target.checked } })}
          isDisabled={disabled}
        >
          Preserve slide aspect ratio
        </Checkbox>
        <Checkbox
          isChecked={p.includeSpeakerNotes}
          onChange={(ev) => onBundleChange({ ...bundle, presentation: { ...p, includeSpeakerNotes: ev.target.checked } })}
          isDisabled={disabled}
        >
          Include speaker notes if available
        </Checkbox>
      </VStack>
    );
  }

  if (category === 'image' && bundle.image) {
    const im = bundle.image;
    return (
      <VStack align="stretch" spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Fit mode</FormLabel>
          <RadioGroup value={im.fitMode} onChange={(v) => onBundleChange({ ...bundle, image: { ...im, fitMode: v as typeof im.fitMode } })} isDisabled={disabled}>
            <Stack spacing={2}>
              <Radio value="fitPage" size="sm">
                Fit page
              </Radio>
              <Radio value="fitWidth" size="sm">
                Fit width
              </Radio>
              <Radio value="original" size="sm">
                Original size
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
        <Checkbox isChecked={im.centerImage} onChange={(ev) => onBundleChange({ ...bundle, image: { ...im, centerImage: ev.target.checked } })} isDisabled={disabled}>
          Center image
        </Checkbox>
        <Checkbox
          isChecked={im.preserveExifOrientation}
          onChange={(ev) => onBundleChange({ ...bundle, image: { ...im, preserveExifOrientation: ev.target.checked } })}
          isDisabled={disabled}
        >
          Preserve EXIF orientation
        </Checkbox>
        <FormControl>
          <FormLabel fontSize="xs">Background</FormLabel>
          <RadioGroup value={im.background} onChange={(v) => onBundleChange({ ...bundle, image: { ...im, background: v as typeof im.background } })} isDisabled={disabled}>
            <Stack spacing={2}>
              <Radio value="white" size="sm">
                White
              </Radio>
              <Radio value="transparent" size="sm">
                Transparent where supported
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
      </VStack>
    );
  }

  if (category === 'html' && bundle.html) {
    const h = bundle.html;
    return (
      <VStack align="stretch" spacing={3}>
        <Checkbox
          isChecked={h.includeBackgroundGraphics}
          onChange={(ev) => onBundleChange({ ...bundle, html: { ...h, includeBackgroundGraphics: ev.target.checked } })}
          isDisabled={disabled}
        >
          Include background graphics
        </Checkbox>
        <Checkbox isChecked={h.printFriendly} onChange={(ev) => onBundleChange({ ...bundle, html: { ...h, printFriendly: ev.target.checked } })} isDisabled={disabled}>
          Print-friendly mode
        </Checkbox>
        <Checkbox isChecked={h.preserveLinks} onChange={(ev) => onBundleChange({ ...bundle, html: { ...h, preserveLinks: ev.target.checked } })} isDisabled={disabled}>
          Preserve links
        </Checkbox>
      </VStack>
    );
  }

  if (category === 'code' && bundle.code) {
    const c = bundle.code;
    return (
      <VStack align="stretch" spacing={3}>
        <Checkbox isChecked={c.syntaxHighlight} onChange={(ev) => onBundleChange({ ...bundle, code: { ...c, syntaxHighlight: ev.target.checked } })} isDisabled={disabled}>
          Syntax highlighting
        </Checkbox>
        <Checkbox isChecked={c.lineNumbers} onChange={(ev) => onBundleChange({ ...bundle, code: { ...c, lineNumbers: ev.target.checked } })} isDisabled={disabled}>
          Line numbers
        </Checkbox>
        <Checkbox isChecked={c.wrapLongLines} onChange={(ev) => onBundleChange({ ...bundle, code: { ...c, wrapLongLines: ev.target.checked } })} isDisabled={disabled}>
          Wrap long lines
        </Checkbox>
        <FormControl>
          <FormLabel fontSize="xs">Theme</FormLabel>
          <RadioGroup value={c.theme} onChange={(v) => onBundleChange({ ...bundle, code: { ...c, theme: v as typeof c.theme } })} isDisabled={disabled}>
            <Stack direction="row" spacing={4}>
              <Radio value="light" size="sm">
                Light
              </Radio>
              <Radio value="dark" size="sm">
                Dark
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
      </VStack>
    );
  }

  return (
    <Text fontSize="sm" color="gray.500">
      Adjust PDF settings and security above.
    </Text>
  );
};
